import { CONSTANTS } from '@toolproof-core/visualization/_data/constants';
import type { CollectionKey, ConstellationKey, ItemKey, SpaceSlug } from '@toolproof-core/visualization/_contract/key';
import type { EntityItemParametricState } from '@toolproof-core/visualization/_contract/item';
import type { EntityItemCollection } from '@toolproof-core/visualization/_contract/collection';
import type { EntityConstellation, EntityConstellationParametricState } from '@toolproof-core/visualization/_contract/constellation';
import type { CameraPose } from '@toolproof-core/visualization/_contract/camera';
import type { PortalSpec } from '@toolproof-core/visualization/_contract/portal';
import type { SpaceDeriver } from '@toolproof-core/visualization/derivation/_contract/derivation';
import { buildPanelShape } from '@toolproof-core/visualization/_pure/shapes';

import { cosmosSpaceUrl } from '@/_pure/cosmosRoute';
import { EPISODE_HUB_GEOMETRY, layoutEpisodeHub } from '@/adapters/episode/_pure/hubLayout';
import type { Config, CosmosDataModel, RuntimeState } from '@/adapters/episode/_contract/config';

const STYLE = {
    background: '#070b16',
    label: { color: '#2f4a7a', text: '#ffffff' },
    guide: { color: '#5d7bb8', opacity: 0.35 },
    portalBySpace: {
        'primitive-space': '#ffb347',
        'workflow-space': '#7fd1ff',
        'core-space': '#c792ff',
    } as Partial<Record<SpaceSlug, string>>,
    portal: '#9fb4e0',
} as const;

export function toEpisodeRingCollectionKey(spaceSlug: SpaceSlug): CollectionKey<'entity'> {
    return `collection:entity:EpisodeRing:${spaceSlug}`;
}

export function toEpisodeRingConstellationKey(spaceSlug: SpaceSlug): ConstellationKey<'entity'> {
    return `constellation:entity:EpisodeRing:${spaceSlug}`;
}

export function toEpisodeRingLabelItemKey(spaceSlug: SpaceSlug): ItemKey<'entity'> {
    return `item:entity:EpisodeRing:${spaceSlug}:label`;
}

export function toEpisodePortalKey(spaceSlug: SpaceSlug, handle: string): string {
    return `portal:episode:${spaceSlug}:${handle}`;
}

function labelState(text: string): EntityItemParametricState {
    return {
        shape: buildPanelShape({ width: EPISODE_HUB_GEOMETRY.labelWidth, height: EPISODE_HUB_GEOMETRY.labelHeight }),
        shapePresentation: { appearanceMode: 'filled' },
        visualStyle: { color: STYLE.label.color },
        interaction: { enabled: false },
        label: { onItem: { text, color: STYLE.label.text, fontSize: EPISODE_HUB_GEOMETRY.labelFontSize } },
    };
}

/** A hub ringed by one carousel of episode portals per space, each carousel named on its hub-facing side. */
export function createSpaceDeriver(): SpaceDeriver<CosmosDataModel, Config, RuntimeState> {
    return (input) => {
        const carousels = layoutEpisodeHub(input.cosmosData.rings);
        const itemCollections: EntityItemCollection[] = [];
        const constellations: EntityConstellation[] = [];
        const entityItemByKey: Record<ItemKey<'entity'>, EntityItemParametricState> = {};
        const entityConstellationByKey: Record<ConstellationKey<'entity'>, EntityConstellationParametricState> = {};
        const portals: PortalSpec[] = [];
        const { labelY } = EPISODE_HUB_GEOMETRY;

        for (const carousel of carousels) {
            const itemCollectionKey = toEpisodeRingCollectionKey(carousel.spaceSlug);
            const constellationKey = toEpisodeRingConstellationKey(carousel.spaceSlug);
            const itemKey = toEpisodeRingLabelItemKey(carousel.spaceSlug);
            entityItemByKey[itemKey] = labelState(carousel.label);
            itemCollections.push({
                itemCollectionKey,
                itemCollectionKind: CONSTANTS.Enums.ItemKind.entity,
                items: [{ itemKey, itemKind: CONSTANTS.Enums.ItemKind.entity, name: carousel.label }],
            });
            constellations.push({
                constellationKind: CONSTANTS.Enums.ItemKind.entity,
                constellationKey,
                itemCollectionKey,
                anchor: { itemKey, anchorKey: 'center' },
            });
            entityConstellationByKey[constellationKey] = {
                layout: {
                    layoutKind: CONSTANTS.Enums.LayoutKind.ring,
                    anchor: { anchorKind: 'position', position: [carousel.center[0], labelY, carousel.center[2]] },
                    radius: carousel.radius,
                    angularPlacement: { placementKind: 'explicit', angleByItemKey: { [itemKey]: carousel.hubwardAngle } },
                    panelFacing: 'outward',
                },
                ringGuide: { y: 0.02 - labelY, color: STYLE.guide.color, opacity: STYLE.guide.opacity },
            };
            const color = STYLE.portalBySpace[carousel.spaceSlug] ?? STYLE.portal;
            for (const portal of carousel.portals) {
                const imageUrl = input.cosmosData.snapshotUrlByHandle[portal.handle];
                portals.push({
                    portalKey: toEpisodePortalKey(carousel.spaceSlug, portal.handle),
                    destination: cosmosSpaceUrl(carousel.spaceSlug, portal.handle),
                    center: portal.center,
                    facing: portal.facing,
                    width: EPISODE_HUB_GEOMETRY.portalWidth,
                    height: EPISODE_HUB_GEOMETRY.portalHeight,
                    title: portal.title,
                    color,
                    ...(imageUrl ? { imageUrl } : {}),
                });
            }
        }

        const eye = EPISODE_HUB_GEOMETRY.eyeHeight;
        const origin = input.config.arrivalOrigin === undefined ? undefined
            : portals.find((portal) => portal.destination === input.config.arrivalOrigin);
        const pose: CameraPose = origin ? resolveReturnArrivalPose(origin)
            : { position: [0, eye, 0], target: [0, eye, -1], fov: 70 };
        return {
            base: {
                spaceKey: input.spaceKey,
                itemCollections,
                constellations,
                itemParametricState: { entityItemByKey },
                itemBehaviorController: { onIntent: () => {} },
                constellationParametricState: { entityConstellationByKey },
                spaceRenderOptions: {
                    backgroundColor: STYLE.background,
                    camera: { pose },
                },
            },
            portals,
        };
    };
}

/** A viewer coming back stands a few steps in front of the portal they left through, facing it. */
export function resolveReturnArrivalPose(portal: Pick<PortalSpec, 'center' | 'facing'>): CameraPose {
    const { eyeHeight, returnArrivalDistance } = EPISODE_HUB_GEOMETRY;
    const [fx, , fz] = portal.facing;
    const position: CameraPose['position'] = [
        portal.center[0] + fx * returnArrivalDistance, eyeHeight, portal.center[2] + fz * returnArrivalDistance,
    ];
    return { position, target: [position[0] - fx, eyeHeight, position[2] - fz], fov: 70 };
}
